/**
 * The pure parts of the live socket: which channels a person listens on, what a message from the
 * socket is allowed to look like, and whether a hint concerns a screen. Kept apart from the
 * connection so they can be tested without a browser, and so the rules a hostile frame is held to
 * are in one short file.
 */

/** One thing that moved. `id` is absent on the courier pool, which names nothing. */
export interface LiveHint {
  topic: string
  id?: string
}

/** The channels a person listens on, by role. Roles with no live screens listen on none. */
export function channelsFor(user: { id?: string | null; role?: string | null } | null | undefined): string[] {
  if (!user?.id) return []

  switch (user.role) {
    case 'agent':
      return [`private-user.${user.id}`]
    case 'livreur':
      return [`private-user.${user.id}`, 'private-pool.livreur']
    case 'platform':
      return [`private-platform.${user.id}`]
    default:
      return []
  }
}

/**
 * Read the payload of a `live.changed` message. Anything that is not a list of `{ topic, id? }` with
 * string values is dropped, so a malformed or hostile frame cannot reach a screen as anything but
 * nothing.
 */
export function parseHints(payload: unknown): LiveHint[] {
  const changes = (payload as { changes?: unknown } | null)?.changes

  if (!Array.isArray(changes)) return []

  return changes.flatMap((change): LiveHint[] => {
    const topic = (change as { topic?: unknown } | null)?.topic
    const id = (change as { id?: unknown } | null)?.id

    if (typeof topic !== 'string' || topic === '') return []

    return [typeof id === 'string' && id !== '' ? { topic, id } : { topic }]
  })
}

/**
 * Does this hint concern a screen? A list shows a topic and takes any hint of it; a detail screen
 * is about one id and takes only a hint for that id (the pool's hints, which name none, are about
 * lists and never about it).
 */
export function hintMatches(
  hint: LiveHint,
  topics: readonly string[],
  wantedId?: string | null,
  isDetail = false
): boolean {
  if (!topics.includes(hint.topic)) return false
  if (!isDetail) return true

  return Boolean(wantedId) && hint.id === wantedId
}
